import React, { useEffect, useState } from "react";
import CartApiService from "../services/CartApiService";
import { Card, Typography, Tag, Checkbox, Button, Space, message, Row, Col, Modal } from "antd";
import { BookOutlined, FileTextOutlined, DeleteOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useAuth } from "../contexts/AuthContext";
import { Role } from "../constants/Role";
import "../styles/Cart.css";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const Cart = () => {
  const [cartData, setCartData] = useState({ courses: [], summary: { totalItems: 0, totalAmount: 0 } });
  const [checkedIds, setCheckedIds] = useState([]);
  const navigate = useNavigate();
  const { hasAnyRole } = useAuth();

  useEffect(() => {
    const fetchCart = async () => {
      // Chỉ fetch cart cho role LEARNER
      if (!hasAnyRole([Role.LEARNER])) {
        setCartData({ courses: [], summary: { totalItems: 0, totalAmount: 0 } });
        setCheckedIds([]);
        return;
      }
      
      const res = await CartApiService.getCart();
      if (res.success) {
        
        const sortedCourses = [...res.data.courses].sort(
          (a, b) => new Date(b.addedAt) - new Date(a.addedAt)
        );
        setCartData({
          ...res.data,
          courses: sortedCourses
        });
        setCheckedIds(sortedCourses.map(c => c._id));
      }
    };
    fetchCart();
  }, [hasAnyRole]);

  const handleCheck = (id) => {
    setCheckedIds(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  // Thêm hàm chọn/bỏ chọn tất cả
  const allChecked = cartData.courses.length > 0 && checkedIds.length === cartData.courses.length;
  const isIndeterminate = checkedIds.length > 0 && checkedIds.length < cartData.courses.length;
  const handleCheckAll = () => {
    if (allChecked) {
      setCheckedIds([]);
    } else {
      setCheckedIds(cartData.courses.map(c => c._id));
    }
  };

  const selectedCourses = cartData.courses.filter(item => checkedIds.includes(item._id));
  const totalAmount = selectedCourses.reduce((sum, item) => sum + item.price, 0);

  const handleContinueShopping = () => {
    navigate("/");
  };

  const handleProceedToCheckout = () => {
    localStorage.setItem('selectedCourseIds', JSON.stringify(checkedIds));
    navigate('/cart/checkout');
  };

  // Hàm xóa khóa học khỏi giỏ hàng
  const handleDelete = (courseId) => {
    // Chỉ cho phép role LEARNER xóa khỏi cart
    if (!hasAnyRole([Role.LEARNER])) {
      message.warning('Only learners can remove courses from cart!');
      return;
    }
    
    Modal.confirm({
      title: 'Remove Course',
      content: 'Are you sure you want to remove this course from your cart?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const res = await CartApiService.removeFromCart(courseId);
          if (res.success) {
            // Cập nhật lại giỏ hàng
            setCartData(prev => ({
              ...prev,
              courses: prev.courses.filter(item => item._id !== courseId),
              summary: {
                ...prev.summary,
                totalItems: prev.summary.totalItems - 1,
                totalAmount: prev.summary.totalAmount - (prev.courses.find(item => item._id === courseId)?.price || 0)
              }
            }));
            setCheckedIds(prev => prev.filter(id => id !== courseId));
            message.success('Course removed from cart!');
            window.dispatchEvent(new Event('cart-changed'));
          } else {
            message.error(res.message || 'Failed to remove course.');
          }
        } catch (err) {
          message.error('Failed to remove course.');
        }
      },
    });
  };

  // Hiển thị UI cart rỗng
  if (cartData.courses.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', background: '#fafbfc' }}>
        <div style={{ background: '#eceef1', borderRadius: '50%', width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 32 }}>
          <img src="https://cdn-icons-png.flaticon.com/512/2038/2038854.png" alt="empty cart" style={{ width: 60, height: 60, opacity: 0.7 }} />
        </div>
        <h2 style={{ fontWeight: 700, fontSize: 28, marginBottom: 8 }}>Your cart is empty</h2>
        <div style={{ color: '#888', fontSize: 17, marginBottom: 32 }}>Looks like you haven't added any courses to your cart yet.</div>
        <Button
          type="primary"
          size="large"
          style={{ background: '#111827', border: 'none', fontWeight: 600, fontSize: 17, borderRadius: 8, padding: '0 32px', height: 48 }}
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
        >
          Continue Shopping
        </Button>
      </div>
    );
  }

  return (
    <div className="cart-bg">
      <div className="cart-list-main-content">
        <div className="cart-header">
          <Title level={2} style={{ marginBottom: 20,marginLeft:20, marginTop: 0, color: '#283593', fontSize: 36, fontWeight: 'bold' }}>Your cart</Title>
        </div>
        <Row gutter={[64, 32]}>
          <Col xs={24} md={18}>
            <Card className="cart-select-all-card" bodyStyle={{ padding: 15 }}>
              
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 12, marginRight: 0}}>
              <Checkbox
                  className="cart-select-all-checkbox cart-checkbox-right"
                  indeterminate={isIndeterminate}
                  checked={allChecked}
                  onChange={handleCheckAll}
                />
                <span style={{ color: '#222', fontWeight: 620, fontSize:18 }}>
                  {allChecked ? 'Deselect all courses' : 'Select all courses'}
                  <span className="cart-select-all-count">
                    ({checkedIds.length}/{cartData.courses.length} courses selected)
                  </span>
                </span>
              </div>
            </Card>
            {cartData.courses.map((item) => (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, marginLeft: 16 }} key={item._id}>
                <Checkbox
                
                  checked={checkedIds.includes(item._id)}
                  onChange={() => handleCheck(item._id)}
                  className="cart-checkbox-item"
                  style={{ marginBottom: 15, marginRight: 16, borderRadius: 60 }}
                />
                <Card
                  className="cart-card"
                  bodyStyle={{ padding: 0 }}
                  onClick={() => handleCheck(item._id)}
                  style={{ cursor: 'pointer', flex: 1 }}
                >
                  <div className="cart-card-content">
                    <div className="cart-card-image-container">
                      <img src={item.imageUrl} alt={item.name} className="cart-card-image" />
                    </div>
                    <div className="cart-card-info" style={{ position: 'relative' }}>
                      <Text className="cart-card-author">by {item.author}</Text>
                      {item.category?.name && (
                        <div style={{ position: 'absolute', top: 0, right: 0 }}>
                          <Tag
                            color="geekblue"
                            style={{
                              display: "inline-block",
                              fontWeight: 500,
                              fontSize: 13,
                              borderRadius: 8,
                              marginTop: 10,
                              padding: "2px 12px",
                              marginRight: 18
                            }}
                          >
                            {item.category.name}
                          </Tag>
                        </div>
                      )}
                      <Title
                        level={4}
                        className="cart-card-title"
                        style={{ cursor: 'pointer' }}
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/courses/${item.slug}`);
                        }}
                      >
                        {item.name}
                      </Title>
                      <Space size="large" className="cart-card-meta">
                        <span>
                          <BookOutlined style={{ color: "#1677ff" }} /> {item.sectionCount} Sections
                        </span>
                        <span>
                          <FileTextOutlined style={{ color: "#1677ff" }} /> {item.lessonCount} Lessons
                        </span>
                      </Space>
                      <div className="cart-card-footer">
                        <Text className="cart-card-price">${item.price}</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={e => {
                              e.stopPropagation();
                              handleDelete(item._id);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </Col>
          <Col xs={24} md={6} style={{ position: 'relative', minHeight: '200px' }}>
            <Card className="cart-summary-card" bodyStyle={{ padding: 24 }}>
              <div className="cart-summary-total-box">
                <Text type="secondary">Total:</Text>
                <div className="cart-summary-total">
                  ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <Text type="secondary" className="cart-summary-selected">
                  {selectedCourses.length} courses selected
                </Text>
              </div>
              <Button
                type="primary"
                block
                size="large"
                disabled={selectedCourses.length === 0}
                className="cart-checkout-btn"
                onClick={handleProceedToCheckout}
              >
                Proceed to Checkout
              </Button>
              <Button
                type="default"
                block
                icon={<ArrowLeftOutlined />}
                className="cart-continue-btn"
                onClick={handleContinueShopping}
              >
                Continue Shopping
              </Button>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Cart;